import { SidebarSimpleIcon, XIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { PANEL_HEADER_SHELL, PANEL_HEADER_TITLE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/**
 * FloatingPanel — shared chrome for the right-slot artifact / context surfaces.
 *
 * Mora has THREE right-side surfaces that share the same visual contract:
 * 1. ProjectHubMount (project context rail on /create)
 * 2. ArtifactDrawer (workflow tracker, image studio, video studio, post editor,
 *    document viewer)
 * 3. GlobalRightDock (engagement, drafts)
 *
 * The reference pattern (Claude Code / Codex right panel): a quiet neutral
 * elevated card separated from the page by an inset gap and restrained border.
 * AppLayout supplies the gap and resize handle; this primitive owns the card
 * surface so artifact, project, engagement, drafts, and agent panels cannot
 * drift into separate shells.
 *
 * The seam with the main column is NOT drawn here. It is owned in ONE place —
 * the `ResizableHandle` between the two panels (AppLayout). The handle owns the
 * interactive grab cue and the panel owns its card edge; `surface="flush"` is
 * reserved for full-bleed mobile Sheet hosts.
 *
 * Usage:
 *   <FloatingPanel aria-label="Workspace">
 *     <FloatingPanelHeader title="News Reaction Pack" onClose={...} />
 *     <FloatingPanelBody>{...content...}</FloatingPanelBody>
 *   </FloatingPanel>
 *
 * Tokens used:
 * - neutral card background so the panel shares the page's color language
 * - thin border + resting elevation for separation without a competing tint
 */

interface FloatingPanelProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  /**
   * Desktop companion panels use the elevated card surface. Mobile Sheets and
   * full-bleed legacy hosts can opt into the same content chrome without
   * painting a second edge around the viewport.
   */
  surface?: 'elevated' | 'flush';
}

export const FloatingPanel = React.forwardRef<HTMLElement, FloatingPanelProps>(
  function FloatingPanel({ children, className, surface = 'elevated', ...props }, ref) {
    return (
      <aside
        ref={ref}
        tabIndex={-1}
        data-panel-surface={surface}
        className={cn(
          'flex h-full flex-col overflow-hidden bg-card focus:outline-none',
          surface === 'elevated'
            ? 'rounded-xl border border-border/60 shadow-elevation-1 dark:border-border/40'
            : 'rounded-none border-0 shadow-none',
          'motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300 motion-safe:ease-fluid',
          className,
        )}
        {...props}
      >
        {children}
      </aside>
    );
  },
);

interface FloatingPanelHeaderProps {
  /** Title text (truncated). Pass null/undefined to hide title slot. */
  title?: React.ReactNode;
  /** Dismiss handler. When provided renders the collapse button on the right. */
  onClose?: () => void;
  /** Custom slot rendered to the LEFT of close (e.g. action menu, tabs). */
  trailing?: React.ReactNode;
  /** Custom aria-label for the close button. */
  closeLabel?: string;
  /** Use a conventional X when the panel is a dismissible workflow surface. */
  closeIcon?: 'collapse' | 'x';
  className?: string;
}

export function FloatingPanelHeader({
  title,
  onClose,
  trailing,
  closeLabel = 'Collapse panel',
  closeIcon = 'collapse',
  className,
}: FloatingPanelHeaderProps): JSX.Element {
  // Aligned to DrawerHeader typography + height so EVERY right-side
  // panel header (project hub rail, workflow tracker, document drawer,
  // brand-guidelines canvas, video studio, plan strategy) reads as
  // the same primitive. h-12 (48px), title sm-medium-foreground/85.
  return (
    <header className={cn(PANEL_HEADER_SHELL, className)}>
      {title !== undefined && title !== null ? (
        <span className={cn(PANEL_HEADER_TITLE, 'flex-1 tracking-tight')}>{title}</span>
      ) : (
        <span aria-hidden className="min-w-0 flex-1" />
      )}
      <div className="flex shrink-0 items-center gap-0.5">
        {trailing}
        {onClose && (
          // Was a hand-rolled <button> at `text-muted-foreground/60` — a
          // DIMMER grey than the `trailing` slot's icon (every caller uses
          // `CircleIconButton`, which rests at full `text-muted-foreground`
          // with a `hover:bg-sidebar-accent` state). Two icons side by side
          // in the same header row read as visibly different colors because
          // they were two different hand-tuned values (caught via screenshot
          // on Engagement Hub, 2026-07-24). Now uses the actual shared
          // primitive — one component, so this can't drift again, and it
          // fixes all three FloatingPanelHeader consumers (ProjectHubMount,
          // ArtifactDrawer, GlobalRightDock) in one place. `size="sm"` also
          // grows the mobile tap target 28px -> 32px, matching every other
          // icon-only header action's mobile-vs-desktop sizing.
          <CircleIconButton size="sm" ariaLabel={closeLabel} tooltip={closeLabel} onClick={onClose}>
            {closeIcon === 'x' ? (
              <XIcon weight="regular" aria-hidden />
            ) : (
              // Collapse glyph for toggle-able side panels; the X variant is
              // reserved for surfaces whose action is simply dismissing them.
              <SidebarSimpleIcon weight="regular" aria-hidden />
            )}
          </CircleIconButton>
        )}
      </div>
    </header>
  );
}

interface FloatingPanelBodyProps {
  children: React.ReactNode;
  className?: string;
}

export function FloatingPanelBody({ children, className }: FloatingPanelBodyProps): JSX.Element {
  // min-h-0 so a tall body scrolls INTERNALLY instead of pushing the panel — a
  // flex-1 child without it won't shrink below its content height.
  return <div className={cn('min-h-0 flex-1 overflow-y-auto', className)}>{children}</div>;
}
